import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiThermometer, FiWind, FiTool, FiShield, 
  FiClock, FiStar, FiCheckCircle, FiPhoneCall, FiChevronDown
} from 'react-icons/fi';
import SafeIcon from '../components/SafeIcon';
import CtaSection from '../components/shared/CtaSection';

const Home = () => {
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 lg:pt-32 lg:pb-48 overflow-hidden bg-primary">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&q=80&w=2000" 
            alt="HVAC Technician working" 
            className="w-full h-full object-cover opacity-30 object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-primary/40"></div>
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/30 border border-secondary/50 text-blue-100 text-sm font-semibold mb-6 backdrop-blur-sm">
                <span className="w-2 h-2 rounded-full bg-accent animate-pulse"></span>
                Top-Rated HVAC in Montreal
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Keeping Montreal Comfortable <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-yellow-400">Year Round.</span>
              </h1>
              <p className="text-lg md:text-xl text-blue-100 mb-10 max-w-2xl leading-relaxed">
                Professional heating, cooling, ventilation, and emergency HVAC services for homes and businesses across the Greater Montreal area.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/quote" className="btn-primary text-base md:text-lg px-8 py-4 w-full sm:w-auto">
                  Get Free Quote
                </Link>
                <a href="tel:5145550122" className="btn-secondary border-white text-white hover:bg-white hover:text-primary text-base md:text-lg px-8 py-4 w-full sm:w-auto">
                  Call Now (514) 555-0122 (demo)
                </a>
              </div>
            </motion.div>

            {/* Hero Highlights Desktop */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="hidden md:flex items-center gap-8 mt-16 pt-8 border-t border-white/20"
            >
              {[
                { icon: FiClock, text: "24/7 Emergency Service" },
                { icon: FiShield, text: "Licensed Technicians" },
                { icon: FiCheckCircle, text: "Financing Available" },
                { icon: FiWind, text: "Fast Response Times" },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-white/90 font-medium">
                  <SafeIcon icon={item.icon} className="text-accent text-xl" />
                  <span>{item.text}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Banner Mobile */}
      <div className="md:hidden bg-secondary text-white py-4 overflow-x-auto">
        <div className="flex px-4 gap-6 min-w-max">
           {[
                { icon: FiClock, text: "24/7 Emergency" },
                { icon: FiShield, text: "Licensed Techs" },
                { icon: FiCheckCircle, text: "Financing" },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm font-medium">
                <SafeIcon icon={item.icon} className="text-accent" />
                <span>{item.text}</span>
              </div>
            ))}
        </div>
      </div>

      {/* Services Section */}
      <section className="section-padding bg-bg relative">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Comprehensive HVAC Services</h2>
            <p className="text-muted text-lg">From beating the summer heat to surviving the harsh Quebec winters, we have the expertise to keep your systems running efficiently.</p>
          </div>

          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {/* Service Card 1 */}
            <motion.div variants={fadeIn} className="bg-white rounded-xl shadow-card hover:shadow-xl transition-shadow p-8 border border-gray-100 group">
              <div className="w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6 group-hover:bg-primary transition-colors">
                <SafeIcon icon={FiThermometer} className="text-3xl text-primary group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold mb-3">Heating Systems</h3>
              <p className="text-muted mb-6">Expert installation, repair, and maintenance for furnaces, boilers, and heat pumps to keep you warm all winter.</p>
              <Link to="/services" className="text-secondary font-semibold flex items-center gap-2 hover:text-primary transition-colors">
                Learn More <SafeIcon icon={FiChevronDown} className="-rotate-90" />
              </Link>
            </motion.div>

            {/* Service Card 2 */}
            <motion.div variants={fadeIn} className="bg-white rounded-xl shadow-card hover:shadow-xl transition-shadow p-8 border border-gray-100 group">
              <div className="w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6 group-hover:bg-primary transition-colors">
                <SafeIcon icon={FiWind} className="text-3xl text-primary group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold mb-3">Air Conditioning</h3>
              <p className="text-muted mb-6">Stay cool during humid Montreal summers with our AC installation, repair, and tune-up services.</p>
              <Link to="/services" className="text-secondary font-semibold flex items-center gap-2 hover:text-primary transition-colors">
                Learn More <SafeIcon icon={FiChevronDown} className="-rotate-90" />
              </Link>
            </motion.div>

            {/* Service Card 3 */}
            <motion.div variants={fadeIn} className="bg-white rounded-xl shadow-card hover:shadow-xl transition-shadow p-8 border border-gray-100 group">
              <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-6 group-hover:bg-accent transition-colors">
                <SafeIcon icon={FiTool} className="text-3xl text-accent group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold mb-3">Emergency Repairs</h3>
              <p className="text-muted mb-6">System broke down in the middle of the night? Our 24/7 rapid response team is ready to restore your comfort.</p>
              <Link to="/services" className="text-accent font-semibold flex items-center gap-2 hover:text-accentHover transition-colors">
                Request Service <SafeIcon icon={FiChevronDown} className="-rotate-90" />
              </Link>
            </motion.div>
          </motion.div>
          
          <div className="text-center mt-12">
            <Link to="/services" className="btn-secondary">View All Services</Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Split Section */}
      <section className="py-0 relative overflow-hidden">
        <div className="flex flex-col lg:flex-row">
          {/* Image Side */}
          <div className="lg:w-1/2 min-h-[400px] lg:min-h-auto relative">
            <img 
              src="https://images.unsplash.com/photo-1581092921461-eab62e97a780?auto=format&fit=crop&q=80&w=1000" 
              alt="Experienced HVAC Technician" 
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          
          {/* Content Side */}
          <div className="lg:w-1/2 bg-white px-6 py-16 lg:p-20 xl:p-24 flex items-center">
            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="max-w-xl"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Why Montreal Trusts NorthPeak</h2>
              <p className="text-muted text-lg mb-8">
                With over 15 years of experience serving the Greater Montreal area, we understand the unique climate challenges our customers face. We don't just fix equipment; we deliver peace of mind.
              </p>
              
              <ul className="space-y-4 mb-10">
                {[
                  "Licensed, Insured, and Certified Technicians (RBQ)",
                  "Upfront, Honest Pricing with No Hidden Fees",
                  "100% Satisfaction Guarantee on Workmanship",
                  "Respect for Your Home (We leave it cleaner than we found it)"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-4">
                    <div className="mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <SafeIcon icon={FiCheckCircle} className="text-lg" />
                    </div>
                    <span className="text-textMain font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              
              <Link to="/about" className="btn-primary">More About Our Company</Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="section-padding bg-bg">
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="flex justify-center mb-4">
              <div className="flex gap-1 text-yellow-400 text-2xl">
                {[1,2,3,4,5].map(i => <SafeIcon key={i} icon={FiStar} className="fill-current" />)}
              </div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Loved by Locals</h2>
            <p className="text-muted text-lg">See what your neighbors are saying about our service.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Review 1 */}
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 relative">
              <div className="flex gap-1 text-yellow-400 mb-4">
                {[1,2,3,4,5].map(i => <SafeIcon key={i} icon={FiStar} className="fill-current text-sm" />)}
              </div>
              <p className="text-textMain italic mb-6">"Our furnace died on the coldest night in January. NorthPeak answered the phone at 2 AM and had a tech out within an hour. Professional, polite, and got the heat back on quickly. Lifesavers!"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center font-bold text-primary">MS</div>
                <div>
                  <h4 className="font-bold text-sm">Marc S.</h4>
                  <p className="text-xs text-muted">West Island, QC</p>
                </div>
              </div>
            </div>

            {/* Review 2 */}
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 relative">
              <div className="flex gap-1 text-yellow-400 mb-4">
                {[1,2,3,4,5].map(i => <SafeIcon key={i} icon={FiStar} className="fill-current text-sm" />)}
              </div>
              <p className="text-textMain italic mb-6">"Got quotes from three different companies for a new heat pump. NorthPeak wasn't the absolute cheapest, but their presentation, knowledge, and warranty options were vastly superior. The installation was flawless."</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center font-bold text-green-700">JL</div>
                <div>
                  <h4 className="font-bold text-sm">Julie L.</h4>
                  <p className="text-xs text-muted">Laval, QC</p>
                </div>
              </div>
            </div>

            {/* Review 3 */}
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 relative">
              <div className="flex gap-1 text-yellow-400 mb-4">
                {[1,2,3,4,5].map(i => <SafeIcon key={i} icon={FiStar} className="fill-current text-sm" />)}
              </div>
              <p className="text-textMain italic mb-6">"I use their Premium Maintenance plan for my commercial property. They are always on time, communicate well, and catch small issues before they become expensive breakdowns. Highly recommend."</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center font-bold text-purple-700">DB</div>
                <div>
                  <h4 className="font-bold text-sm">David B.</h4>
                  <p className="text-xs text-muted">Downtown Montreal</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-12 bg-white border-y border-gray-100">
        <div className="container-custom">
          <p className="text-center text-sm font-bold text-muted uppercase tracking-wider mb-8">We Service & Install Top Brands</p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-60 grayscale hover:grayscale-0 transition-all duration-300">
             {/* Placeholder for logos using text since we don't have brand image assets */}
             <span className="text-2xl font-black font-heading">LENNOX</span>
             <span className="text-2xl font-black font-heading">Carrier</span>
             <span className="text-2xl font-black font-heading tracking-tighter">Trane</span>
             <span className="text-2xl font-black font-heading text-blue-800">DAIKIN</span>
             <span className="text-2xl font-black font-heading">GOODMAN</span>
          </div>
        </div>
      </section>

      <CtaSection />
    </div>
  );
};

export default Home;